import { useEffect, useState } from 'react';
import { Badge, Box, Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import { useReducedMotion } from '@mantine/hooks';
import {
  FORMAT_RULES,
  type FormatRules,
  type InteractionContent,
  type Question,
} from '@quizee/core';
import type { InteractionResults } from '../api/types';
import { percentage } from '../format';
import { ErrorNote } from './ErrorNote';
import { Loading } from './Loading';

/**
 * What has come back, read from the aggregate the projector maintains.
 *
 * Two different things are on this screen depending on the format, and saying which is the point
 * of the panel. A poll or a quiz has results readers see, so this is a preview of them. A survey
 * or a form shows readers nothing at all, so what is here is for the newsroom only, and the panel
 * says that rather than leaving an editor to wonder what their readers can see.
 *
 * Only choice answers are counted. Typed answers go to the vault and scale answers carry a number,
 * and neither folds into a count of option ids, so those questions say so instead of showing a
 * row of zeroes.
 *
 * Refreshing is a button rather than a timer: an editor watching a poll should decide when to
 * look, and a dashboard left open overnight should not keep asking.
 */
export function ResultsPanel({
  content,
  results,
  problem,
  loading,
  onRefresh,
}: {
  content: InteractionContent;
  results: InteractionResults | null;
  problem: string | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  const rules = FORMAT_RULES[content.format];
  const name = rules.label.toLowerCase();
  const single = content.questions.length === 1;
  const total = results?.total_responses ?? 0;

  return (
    <Card component="section" withBorder radius="lg" p="lg" mt="xl">
      <Group justify="space-between" align="center" gap="md">
        <Title order={2} size="h3">
          {rules.hasResults ? 'Results' : 'Responses'}
        </Title>
        <Button variant="default" onClick={onRefresh} disabled={loading}>
          {loading ? 'Refreshing...' : `Refresh ${rules.hasResults ? 'results' : 'responses'}`}
        </Button>
      </Group>

      <Text size="sm" mt="xs">
        {caveat(rules, name)}
      </Text>

      {problem !== null && <ErrorNote message={problem} onRetry={onRefresh} />}
      {problem === null && results === null && loading && (
        <Loading label={rules.hasResults ? 'Loading results' : 'Loading responses'} />
      )}

      {results !== null && (
        <>
          <Text c="dimmed" size="sm" mt="sm">
            {summary(total, single)}
          </Text>

          <Stack gap="md" mt="md">
            {content.questions.map((question, index) => (
              <QuestionResults
                key={question.id}
                question={question}
                index={index}
                showPrompt={!single}
                showCorrect={rules.requiresCorrectAnswers}
                counts={results.counts}
              />
            ))}
          </Stack>
        </>
      )}
    </Card>
  );
}

/**
 * What these counts are, and what they are not.
 *
 * Only choice answers reach the aggregate, so a quiz's scores and a personality quiz's outcomes
 * are worked out in the reader's browser and never counted here. An editor looking at a panel
 * headed Results is entitled to know that before they read a number as something it is not.
 */
function caveat(rules: FormatRules, name: string): string {
  if (!rules.hasResults) {
    return `Readers see no results from a ${name}, only your finishing message. What follows is for you.`;
  }
  if (rules.requiresOutcomes) {
    return 'Which outcome readers landed on is not counted yet. What follows is the answers they chose.';
  }
  if (rules.requiresCorrectAnswers) {
    return 'Scores are not counted yet. What follows is how readers answered each question.';
  }
  return 'This is what readers see once they have answered.';
}

/**
 * One question's answers.
 *
 * The share is worked out against this question's own answers rather than against the whole
 * interaction, because a reader who gave up after question one should not make question five's
 * percentages read as though nobody answered it.
 */
function QuestionResults({
  question,
  index,
  showPrompt,
  showCorrect,
  counts,
}: {
  question: Question;
  index: number;
  showPrompt: boolean;
  showCorrect: boolean;
  counts: Readonly<Record<string, number>>;
}) {
  const answered = question.options.reduce(
    (running, option) => running + (counts[option.id] ?? 0),
    0,
  );

  return (
    <div>
      {showPrompt && (
        <Text fw={500} size="sm" mb={4}>
          {index + 1}. {question.prompt}
        </Text>
      )}

      {question.options.length === 0 ? (
        <Text c="dimmed" size="sm">
          {question.type === 'free_text'
            ? 'Typed answers are not counted here.'
            : 'Answers on a scale are not counted here.'}
        </Text>
      ) : (
        <Box component="ul" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {question.options.map((option) => {
            const count = counts[option.id] ?? 0;
            const share = percentage(count, answered);
            return (
              <Box component="li" key={option.id} py={6}>
                <Group gap="sm" wrap="nowrap" align="center">
                  <Text size="sm" style={{ flex: '0 0 40%', minWidth: 0 }}>
                    {option.label}
                    {showCorrect && option.is_correct === true && (
                      <Badge
                        ml="xs"
                        size="xs"
                        radius="sm"
                        variant="light"
                        color="teal"
                        style={{ verticalAlign: 'middle' }}
                      >
                        Correct
                      </Badge>
                    )}
                  </Text>
                  <ResultBar share={share} />
                  <Text size="sm" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {count} ({share}%)
                  </Text>
                </Group>
              </Box>
            );
          })}
        </Box>
      )}
    </div>
  );
}

/**
 * The count under the heading.
 *
 * With one question every answer is one reader, so it can be said in those words. With several,
 * the aggregate counts answers rather than people, and claiming otherwise would overstate what
 * the projector actually knows.
 */
function summary(total: number, single: boolean): string {
  if (single) {
    if (total === 0) return 'No responses yet.';
    return `${String(total)} ${total === 1 ? 'response' : 'responses'} so far.`;
  }
  if (total === 0) return 'No answers yet.';
  return `${String(total)} ${total === 1 ? 'answer' : 'answers'} counted so far.`;
}

/**
 * The one animation in the dashboard.
 *
 * The bar mounts empty and grows to its share, which is the moment the editor is waiting for when
 * they publish something. Anyone who has asked their system for less motion gets the finished bar
 * straight away: the CSS transition is guarded in styles.css, and the width is set without a
 * frame's delay here so nothing moves at all.
 */
function ResultBar({ share }: { share: number }) {
  // Read on the first render rather than in an effect: somebody who has asked for less motion
  // should not get one frame of it before the setting is noticed.
  const reducedMotion = useReducedMotion(undefined, { getInitialValueInEffect: false });
  const animate = !reducedMotion;
  const [width, setWidth] = useState(animate ? 0 : share);

  useEffect(() => {
    if (!animate) {
      setWidth(share);
      return;
    }
    const frame = requestAnimationFrame(() => setWidth(share));
    return () => cancelAnimationFrame(frame);
  }, [animate, share]);

  return (
    <Box
      aria-hidden="true"
      style={{
        flex: 1,
        height: 10,
        borderRadius: 999,
        overflow: 'hidden',
        background: 'var(--mantine-color-default-border)',
      }}
    >
      <Box
        className="quizee-bar-fill"
        data-motion={animate ? 'on' : 'off'}
        style={{
          width: `${String(width)}%`,
          height: '100%',
          borderRadius: 999,
          background: 'var(--mantine-color-coral-filled)',
        }}
      />
    </Box>
  );
}
